import React, {useEffect, useState} from 'react';
import {PlusIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import {DateTime} from "luxon";
import {invalidate} from "services/query-client";
import Button from "components/Button";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useOperator} from "context/OperatorContext";
import FloatingDropdown from "../../../components/FloatingDropdown";
import DayView from "./DayView";
import WeekView from "./WeekView";
import MonthView from "./MonthView";
import DeleteEvent from "../Delete";
import {useNotifications} from "../../../context/NotificationContext";
import {Event} from 'types/Event';
import {useCalendarItems} from "../../../hooks/useCalendarItems";
import DateSelector from "./DateSelector";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import {useQuery} from "react-query";
import ngage from "../../../services/ngage";
import {Site} from "../../../types/Site";
import {EventType} from "../../../types/EventType";
import {isValidSiteId} from "../../../services/site";
import {isValidEventTypeId} from "../../../services/events";
import {getStoredItem, setStoredItem, STORAGE_KEYS} from "../../../services/storage";

// Constants ----

const calendarViews = ['day', 'week', 'month'] as const

// Types ----

type SiteItem = {
  label: string
  value: string | null
}

type TypeItem = {
  label: string
  value: string | null
}

export type CalendarView = typeof calendarViews[number];

type CalendarViewItem = {
  label: string
  value: CalendarView
}

const calendarViewItems: Array<CalendarViewItem> = [
  {label: 'Day', value: 'day'},
  {label: 'Week', value: 'week'},
  {label: 'Month', value: 'month'},
]

// Component ----

function EventSchedule() {
  const {termFor, isMultiSite} = useOperator();
  const auth = useAuth();
  const navigate = useNavigate();
  const {notify} = useNotifications();
  const [searchParams, setSearchParams] = useSearchParams({
    date: DateTime.now().startOf('day').toFormat('yyyy-MM-dd'),
    view: getStoredView() ?? 'week',
    site: '',
    type: '',
  });

  const [deletingEvent, setDeletingEvent] = useState<Event | null>(null);

  const dateParam = searchParams.get('date') ?? '';
  const viewParam = (searchParams.get('view') ?? 'week') as CalendarView;
  const siteIdParam = searchParams.get('site') ?? '';
  const typeIdParam = searchParams.get('type') ?? '';

  const dateParamDateTime = DateTime.fromISO(dateParam).startOf('day');
  const date = dateParamDateTime.isValid ? dateParamDateTime : DateTime.now().startOf('day');
  const view = calendarViews.includes(viewParam) ? viewParam : getStoredView() ?? 'week';

  const {data: sites, isLoading: sitesLoading} = useQuery(['sites.filtered'], () => ngage.sites.filtered());
  const {data: eventTypes, isLoading: eventTypesLoading} = useQuery(['eventTypes.all'], () => ngage.eventTypes.all());

  const siteId = siteIdParam ? Number(siteIdParam) : null;
  const typeId = typeIdParam ? Number(typeIdParam) : null;
  const {dateIntervals, eventList, isLoading, queryKey, refetchEvents, isFetchingEvents, eventsUpdatedAt} = useCalendarItems(date, view, siteId, typeId);

  // Check URL params or local storage for selected site id when sites are loaded
  useEffect(() => {
    if (!sites?.data) return;

    const storedSiteId = getStoredItem(STORAGE_KEYS.EVENTS_SITE_ID);
    const defaultSiteId = storedSiteId ?? sites.data[0].id.toString();
    const siteId = isValidSiteId(sites.data, siteIdParam) ? siteIdParam : defaultSiteId;

    setSearchParams(currentParams => {
      currentParams.set('site', siteId);
      return currentParams;
    }, {replace: true});
  }, [siteIdParam, sites?.data, setSearchParams])

  // Check URL params or local storage for selected event type id when event types are loaded
  useEffect(() => {
    if (!eventTypes?.data?.length) return;

    const storedEventTypeId = getStoredItem(STORAGE_KEYS.EVENTS_TYPE_ID);
    const defaultEventTypeId = storedEventTypeId ?? eventTypes.data[0].id.toString();
    const typeId = isValidEventTypeId(eventTypes.data, typeIdParam) ? typeIdParam : defaultEventTypeId;

    setSearchParams(currentParams => {
      currentParams.set('type', typeId);
      return currentParams;
    }, {replace: true});
  }, [typeIdParam, eventTypes?.data, setSearchParams])

  // Refetch events at a specific interval
  useEffect(() => {
    if (isFetchingEvents) return;

    const interval = setInterval(refetchEvents, 60000);

    return () => clearInterval(interval);
  }, [isFetchingEvents, refetchEvents]);

  // Create site items for dropdown
  function getSiteItems(sites: Site[] = []): SiteItem[] {
    return sites.map(site => ({label: site.name, value: site.id.toString()}));
  }

  // Create event type items for dropdown
  function getTypeItems(types: EventType[] = []): TypeItem[] {
    const allTypes: TypeItem = {label: 'All types', value: null};
    const dynamicTypes = types.map(type => ({label: type.name, value: type.id.toString()}));

    return [allTypes, ...dynamicTypes];
  }

  // Get site items for dropdown
  const siteItems = sites?.data ? getSiteItems(sites.data) : [];

  // Get site items for dropdown
  const typeItems = eventTypes?.data ? getTypeItems(eventTypes.data) : [];

  // Prefetch next and previous days/weeks/months
  // TODO: Temporarily disabled due to performance issues
  // if (view === 'DAY') prefetchNearbyDays(date, siteIdParam);
  // if (view === 'WEEK') prefetchNearbyWeeks(date, siteIdParam);
  // if (view === 'MONTH') prefetchNearbyMonths(date, siteIdParam);

  // Set selected site
  function selectSite(siteId: number | null) {
    const siteIdString = siteId?.toString() ?? '';
    setSearchParams(currentParams => {
      currentParams.set('site', siteIdString);
      return currentParams;
    }, {replace: true});
    setStoredItem(STORAGE_KEYS.EVENTS_SITE_ID, siteIdString);
  }

  // Set event type
  function selectEventType(eventTypeId: number | null) {
    const eventTypeIdString = eventTypeId?.toString() ?? '';
    setSearchParams(currentParams => {
      currentParams.set('type', eventTypeIdString);
      return currentParams;
    }, {replace: true});
    setStoredItem(STORAGE_KEYS.EVENTS_TYPE_ID, eventTypeIdString);
  }

  // Set selected calendar view
  function selectView(view: CalendarView) {
    setSearchParams(currentParams => {
      currentParams.set('view', view);
      return currentParams;
    }, {replace: true});
    setStoredItem(STORAGE_KEYS.EVENTS_CALENDAR_VIEW, view);
  }

  // Set selected date
  function selectDate(date: DateTime) {
    const startOfDay = date.startOf('day').toFormat('yyyy-MM-dd');
    setSearchParams(currentParams => {
      currentParams.set('date', startOfDay);
      return currentParams;
    }, {replace: true});
  }

  // Check local storage for calendar view
  function getStoredView() {
    const view = getStoredItem(STORAGE_KEYS.EVENTS_CALENDAR_VIEW) as CalendarView | null;

    if (calendarViews.includes(view as CalendarView)) return view;

    return null;
  }

  function formatSelectedDate(date: DateTime) {
    let formatString = ''

    switch (view) {
      case 'day':
        formatString = 'DD'
        break;
      case 'week':
        formatString = 'LLLL yyyy'
        break;
      case 'month':
        formatString = 'LLLL yyyy'
        break;
    }

    return date.toFormat(formatString)
  }

  // Prevent unauthorised viewing
  if (auth.cannot('events:read')) {
    return <NotAuthorised/>
  }

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('events:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/events/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {
    'App Content': '/',
    [termFor('Events')]: '/',
    'Calendar': '/events'
  }

  return (
    <Page
      title='Calendar'
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={sitesLoading || eventTypesLoading}
    >
      <div className="flex flex-col">
        <header className="relative border-b border-gray-200 pb-4">
          <div className="flex items-start justify-between">
            <div className='flex flex-col gap-3'>
              {/* Current Month */}
              <h2 className="text-lg font-medium text-gray-900">
                <time dateTime={formatSelectedDate(date)}>{formatSelectedDate(date)}</time>
              </h2>

              <div className="flex items-center gap-2">
                {/* Refresh button */}
                <Button
                  onClick={() => refetchEvents()}
                  showSpinner={isFetchingEvents}
                  disabled={isFetchingEvents}
                  size={Button.sizes.TINY}
                  color={Button.colors.SECONDARY}
                >
                  {isFetchingEvents ? 'Refreshing' : 'Refresh'}
                </Button>

                {!!eventList && !!eventsUpdatedAt && <p className='text-xs text-gray-500'><span className='text-black'>Updated:</span> {eventsUpdatedAt}</p>}
              </div>
            </div>

            {/* Calendar actions and buttons */}
            <div className="flex items-center gap-2">
              {/* View Selector */}
              <FloatingDropdown
                items={calendarViewItems}
                selectedIndex={calendarViewItems.findIndex((item) => item.value === view)}
                onSelect={(item) => selectView(item.value)}
              />

              {/* Date Selector */}
              <DateSelector calendarView={view} selectDate={selectDate} selectedDate={date}/>
            </div>

          </div>

          <div className="flex items-end gap-2">
            {/* Event type selector */}
            <div className='ml-auto'>
              <FloatingDropdown
                label='Type'
                items={typeItems}
                selectedIndex={Math.max(typeItems.findIndex((item) => item.value === typeIdParam), 0)}
                onSelect={(item) => selectEventType(item.value)}
              />
            </div>

            {/* Site selector */}
            {isMultiSite &&
              <div>
                <FloatingDropdown
                  label={termFor('Site')}
                  items={siteItems}
                  selectedIndex={siteItems.findIndex((item) => item.value === siteIdParam) || 0}
                  onSelect={(item) => selectSite(item.value)}
                />
              </div>
            }
          </div>
        </header>

        {/* Calendar Views */}
        {view === 'day' &&
          <DayView
            selectedDate={date}
            selectDate={selectDate}
            events={eventList}
            isLoading={isLoading}
            onDeleteEvent={setDeletingEvent}
            calendarView={view}
          />
        }
        {view === 'week' &&
          <WeekView
            selectedDate={date}
            selectDate={selectDate}
            dates={dateIntervals}
            events={eventList}
            isLoading={isLoading}
            onDeleteEvent={setDeletingEvent}
            calendarView={view}
          />
        }
        {view === 'month' &&
          <MonthView
            selectedDate={date}
            selectDate={selectDate}
            selectView={selectView}
            dates={dateIntervals}
            events={eventList}
            isLoading={isLoading}
            onDeleteEvent={setDeletingEvent}
          />
        }
      </div>

      {/* Delete confirmation modal */}
      <DeleteEvent
        event={deletingEvent}
        show={!!deletingEvent}
        onCancel={() => setDeletingEvent(null)}
        onDelete={event => {
          invalidate(queryKey).catch()
          setDeletingEvent(null)
          notify("success", `${termFor('Event')} Deleted`, `${event.name} has been permanently removed`)
        }}
        onDeleteFail={event => {
          setDeletingEvent(null)
          notify("error", `${termFor('Event')} Not Deleted`, `${event.name} could not be removed`)
        }}
      />
    </Page>
  );
}

export default EventSchedule;